import React, { createContext, useState, useEffect, ReactNode } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

type ThemeContextType = {
  darkMode: boolean;
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
};

export const ThemeContext = createContext<ThemeContextType>({
  darkMode: false,
  themeMode: 'system',
  toggleTheme: () => { },
  setThemeMode: () => { },
});

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {

  // Obtener el modo de tema guardado o usar 'system' por defecto
  const savedThemeMode = localStorage.getItem('themeMode') as ThemeMode || 'system';
  const [themeMode, setThemeModeState] = useState<ThemeMode>(savedThemeMode);

  // Determinar si está en modo oscuro basado en el modo de tema
  const [darkMode, setDarkMode] = useState<boolean>(
    themeMode === 'dark' ||
    (themeMode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  );

  // Actualizar el DOM y localStorage cuando cambia el modo oscuro
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Escuchar cambios en las preferencias del sistema
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      if (themeMode === 'system') {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Actualizar el modo oscuro cuando cambia el modo de tema
  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);

    if (themeMode === 'dark') {
      setDarkMode(true);
    } else if (themeMode === 'light') {
      setDarkMode(false);
    } else if (themeMode === 'system') {
      setDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, [themeMode]);

  // Alternar entre modo claro y oscuro
  const toggleTheme = () => {
    if (themeMode === 'system') {
      setThemeMode(darkMode ? 'light' : 'dark');
    } else {
      setThemeMode(themeMode === 'dark' ? 'light' : 'dark');
    }
  };

  // Establecer el modo de tema
  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
  };

  return (
    <ThemeContext.Provider value={{ darkMode, themeMode, toggleTheme, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
